import { formatCurrency } from "@sellar-io/common";
import { usePayoutStats } from "../../../../hooks/api/v2/payout/usePayoutStats";
import dayjs from "dayjs";
import { Tooltip } from "@sellar-io/components";
import { TooltipTrigger } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";

import { Icon, H5 } from "@sellar-io/components";
import { useNavigate, useSearchParams } from "react-router";
import * as ROUTES from "../../../../constants/routes";
import { useAllOrdersListStore } from "../../../order/list/AllOrders/AllOrdersListStore";
import type {
  PayoutListResponseType,
  NextPayoutResponseType,
} from "@sellar-io/api-v2/types";
import { useNextPayout } from "../../../../hooks/api/v2/payout/useNextPayout";
import { useEffect } from "react";

export const Dot = ({ color }: { color: string }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${color}`}></span>
);

const StatsTip = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <TooltipContent
      className={
        "Tooltip mt-5 w-[300px] rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-lg"
      }
    >
      <div className="flex gap-2">
        <div className="flex shrink-0 items-center">
          <Icon size={0.75} icon={"INFORMATION_OUTLINE"} color={"#27272A"} />
        </div>
        <div>
          <H5 className="text-sm font-semibold text-zinc-800">{title}</H5>
          <p className="text-xs text-zinc-700">{description}</p>
        </div>
      </div>
      <div className="absolute top-[-7px] left-1/2 -translate-x-1/2">
        <div className="h-3 w-3 rotate-45 transform border-t border-l border-gray-300 bg-white"></div>
      </div>
    </TooltipContent>
  );
};

const StatsSkeleton = () => {
  // We have 4 panels: running, next, last, all-time
  return (
    <div className="mt-2 inline-flex animate-pulse rounded-lg border border-zinc-300 bg-white py-4 md:mt-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className={`flex min-w-[186px] flex-col gap-2 ${
            i < 3 ? "border-r" : ""
          } px-6`}
        >
          {/* title placeholder */}
          <div className="h-4 w-3/5 rounded bg-gray-200" />

          {/* main stat placeholder */}
          <div className="h-6 w-2/3 rounded bg-gray-200" />

          {/* extra line only on the first panel */}
          {i === 0 && <div className="h-3 w-1/4 rounded bg-gray-200" />}
        </div>
      ))}
    </div>
  );
};

const InteractiveStatTitle = ({ title }: { title: string }) => {
  return (
    <span className="flex items-center text-xs font-semibold text-nowrap">
      <span className="group-hover:underline">{title}</span>
      <Icon icon="CHEVRON_RIGHT" size={0.7} className="m-0" />
    </span>
  );
};

export const SellarCreditStats = ({
  setActive,
  setSelectedPayout,
}: {
  setSelectedPayout: (
    payout: PayoutListResponseType[number] | NextPayoutResponseType,
  ) => void;
  setActive: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: stats, isPending } = usePayoutStats();

  const { data: nextPayoutItems } = useNextPayout();
  const nextPayoutExists = nextPayoutItems && nextPayoutItems.Invoices;

  const goToNextPayout = searchParams.get("payout") === "next";

  useEffect(() => {
    if (goToNextPayout && nextPayoutExists) {
      setActive(true);
      setSelectedPayout(nextPayoutItems);
    }
  }, [
    goToNextPayout,
    nextPayoutExists,
    setActive,
    setSelectedPayout,
    nextPayoutItems,
  ]);

  if (isPending) {
    return <StatsSkeleton />;
  }

  if (!stats) {
    return null;
  }

  const {
    lastPayout,
    nextPayout,
    runningTotal,
    totalPayouts,
    ordersToBeDispatched,
  } = stats;

  return (
    <div className="mt-2 inline-flex rounded-lg border border-zinc-300 bg-white py-4 md:mt-6">
      <Tooltip placement="bottom">
        <TooltipTrigger>
          <div
            className="group flex min-w-[186px] flex-col gap-2 border-r px-6 hover:cursor-pointer"
            onClick={() => {
              useAllOrdersListStore.getState().reset();
              useAllOrdersListStore.getState().setPaymentType("sellarCredit");
              navigate(`${ROUTES.ORDERS}?tab=All+orders`);
            }}
          >
            <InteractiveStatTitle title="Running total this week" />
            <div>
              <div className="flex items-center gap-1">
                <img
                  src="https://storage.googleapis.com/sellar-partner-images/sellar-image-assets/app-assets/running-total.svg"
                  alt=""
                  className="h-3 w-3 rounded-full"
                />
                <span className="text-xl text-nowrap">
                  {formatCurrency(runningTotal)}
                </span>
              </div>
              {ordersToBeDispatched > 0 && (
                <div className="text-xs">
                  <span className="text-emerald-600">
                    +{ordersToBeDispatched}
                  </span>
                  <span className="text-nowrap">
                    &nbsp;orders to be dispatched
                  </span>
                </div>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <StatsTip
          title="Running total"
          description="Invoices are issued on order dispatch."
        />
      </Tooltip>
      <div
        className={`group flex min-w-[186px] flex-col gap-2 border-r px-6 ${
          nextPayoutExists ? "hover:cursor-pointer" : ""
        }`}
        onClick={() => {
          if (nextPayoutExists) {
            setActive(true);
            setSelectedPayout(nextPayoutItems);
          }
        }}
      >
        <InteractiveStatTitle title="Current balance" />
        <div className="flex items-center gap-1 text-xl">
          <Dot color="bg-amber-600" />
          <span className="text-xl text-nowrap">
            {formatCurrency(nextPayout.total)}
          </span>
        </div>
      </div>
      <div className="flex min-w-[186px] flex-col gap-2 border-r px-6">
        <span className="text-xs font-semibold text-nowrap">
          Last payout:{" "}
          {lastPayout.date ? dayjs(lastPayout.date).format("DD/MM/YYYY") : ""}
        </span>
        <span className="flex items-center gap-1 text-xl">
          <Dot color="bg-emerald-800" />
          <span className="text-nowrap text-zinc-800">
            {formatCurrency(lastPayout.total)}
          </span>
        </span>
      </div>
      <div className="flex min-w-[186px] flex-col gap-2 px-6">
        <span className="text-xs font-semibold text-nowrap">
          All-time payouts:
        </span>
        <span className="flex items-center gap-1 text-xl">
          <Dot color="bg-emerald-800" />
          <span className="text-nowrap text-zinc-800">
            {formatCurrency(totalPayouts)}
          </span>
        </span>
      </div>
    </div>
  );
};
